import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { Box, ClickAwayListener, Stack, Tooltip, Typography } from "~/common/mui";
import { C_ON_SURFACE, C_OUTLINE_VARIANT, C_SURFACE, C_WHITE } from "../icon/Colors";
import { ELEVATION_2 } from "../icon/Elevation";
import { CORNER_EXTRA_SMALL } from "../icon/Corners";
import { FontSize, getFontStyle } from "../materialInput/Fonts";

/**
 * RichTooltip component renders a customizable tooltip that can either behave as a
 * standard tooltip or a "rich" tooltip with additional action buttons.
 *
 * Usage:
 * - `content`: The main content to display inside the tooltip. Can be a string or any React node.
 * - `actions`: Optional array of React nodes (buttons, icons, etc.) to render as actions inside the tooltip.
 *              If provided, the tooltip will use a richer styling.
 * - `actionPosition`: Defines the alignment of the actions, either "start" (left) or "end" (right). Default is "start".
 * - `disableHover`: When true, disables the default hover behavior and requires manual open/close control.
 *                   The tooltip opens on click and closes via the exposed `close` method through the `ref`.
 *                   This allows external control over the tooltip visibility. (for example check competences method of SPCompanyDetails.tsx)
 *
 * This component uses MUI's Tooltip and supports forwarding a ref to expose the following methods:
 * - `open()`: Programmatically opens the tooltip.
 * - `close()`: Programmatically closes the tooltip.
 *
 * Example of external control usage:
 * const tooltipRef = useRef<RichTooltipHandle>(null);
 * tooltipRef.current?.open();
 * tooltipRef.current?.close();
 *
 * The component also includes a ClickAwayListener when `disableHover` is true to close the tooltip
 * when clicking outside the tooltip area.
 */

type RichTooltipProps = {
  content: React.ReactNode;
  actions?: React.ReactNode[];
  children: React.ReactNode;
  actionPosition?: "start" | "end";
  disableHover?: boolean;
};

export const tooltipSlotProps = {
  tooltip: {
    sx: {
      padding: "12px 16px",
      backgroundColor: C_SURFACE,
      boxShadow: ELEVATION_2,
      borderRadius: "8px",
      color: C_ON_SURFACE,
      pointerEvents: "auto",
    },
  },
};
const simpletooltipProps = {
  tooltip: {
    sx: {
      padding: "8px",
      borderRadius: CORNER_EXTRA_SMALL,
      backgroundColor: C_WHITE,
      color: C_ON_SURFACE,
      border: `1px solid ${C_OUTLINE_VARIANT}`,
      ...getFontStyle(FontSize.BodySmall),
    },
  },
};

export type RichTooltipHandle = {
  open: () => void;
  close: () => void;
};

const RichTooltip = forwardRef<RichTooltipHandle, RichTooltipProps>(
  ({ content, actions = [], actionPosition = "start", disableHover = false, children }, ref) => {
    const [open, setOpen] = React.useState(false);

    const tooltipRef = useRef<HTMLDivElement | null>(null);

    const handleTooltipClose = () => setOpen(false);
    const handleTooltipOpen = () => setOpen(true);

    // Expose methods to use outside
    useImperativeHandle(ref, () => ({
      open: handleTooltipOpen,
      close: handleTooltipClose,
    }));

    const tooltipContent = (
      <Stack
        gap={1}
        ref={tooltipRef} //ref here so we can detect clicks inside tooltip content
      >
        {typeof content === "string" ? <Typography>{content}</Typography> : content}
        {actions.length > 0 && (
          <Stack direction="row" gap={1} justifyContent={actionPosition === "start" ? "flex-start" : "flex-end"}>
            {actions.map((action, index) => (
              <Box key={index}>{action}</Box>
            ))}
          </Stack>
        )}
      </Stack>
    );

    const tooltipWithWrappedContent = (
      <Tooltip
        ref={ref}
        title={tooltipContent}
        open={disableHover ? open : undefined}
        disableFocusListener={disableHover}
        disableHoverListener={disableHover}
        disableTouchListener={disableHover}
        onClose={disableHover ? handleTooltipClose : undefined}
        slotProps={actions.length > 0 ? tooltipSlotProps : simpletooltipProps}
      >
        <Box
          onClick={disableHover ? handleTooltipOpen : undefined}
          sx={{
            display: "flex",
            cursor: "pointer",
            width: "fit-content",
          }}
        >
          {children}
        </Box>
      </Tooltip>
    );

    // Custom onClickAway handler to only close if clicked outside tooltip
    const handleClickAway = (event: MouseEvent | TouchEvent) => {
      if (
        tooltipRef.current &&
        (tooltipRef.current === event.target || tooltipRef.current.contains(event.target as Node))
      ) {
        return;
      }

      handleTooltipClose();
    };

    return disableHover ? (
      <ClickAwayListener onClickAway={handleClickAway}>{tooltipWithWrappedContent}</ClickAwayListener>
    ) : (
      tooltipWithWrappedContent
    );
  },
);

export default RichTooltip;
